import { sliceUtf16Safe } from "@openclaw/normalization-core/utf16-slice";
import { UPDATE_RUN_PHASES } from "../../packages/gateway-protocol/src/update-run-vocabulary.js";
import {
  formatUpdateActivationTimeoutGuidance,
  isVerifiedUpdateRollback,
  UPDATE_ACTIVATION_TIMEOUT_REASON,
  UPDATE_FOREIGN_DESTINATION_REASON,
  UPDATE_INSTALL_SKIP_GUIDANCE,
} from "../shared/update-outcome.js";
import { formatDurationPrecise } from "./format-time/format-duration.ts";
import type { RestartSentinelPayload } from "./restart-sentinel-store.js";
import { formatUpdateDoctorConfigWriteRefusal } from "./update-doctor-config.js";
import {
  formatUpdateFailureFact,
  selectUpdateFailureReportSteps,
  UPDATE_DESTINATION_RECOVERY,
} from "./update-failure-facts-format.js";
import {
  LEGACY_UPDATE_RUN_ADVISORY,
  LEGACY_UPDATE_RUN_EXPIRED_REASON,
} from "./update-run-legacy-expiry.js";
import { isAcknowledgedAbandonedUpdateRun, type UpdateRunRecord } from "./update-run-record.js";
import type { UpdateRunReportHealth } from "./update-run-report-health.js";
import { updateRunStepsFromResultStep, updateRunWarningMessages } from "./update-run-step.js";
import type { UpdateRunResult } from "./update-runner-types.js";
import { formatUpdateSnapshotCapacity } from "./update-snapshot-capacity.js";

export type UpdateRunReport = { headline: string; lines: string[]; markdown: string };

const IN_PROGRESS_REPORT_PREFIX = "⬆️ OpenClaw update in progress: ";
const FAILURE_RECOVERY_HINTS: Readonly<Record<string, string>> = {
  "preflight-insufficient-space":
    "Free space on the preflight staging and package-manager store filesystems, then rerun the update.",
  "pnpm-corepack-missing":
    "This pnpm checkout could not auto-enable pnpm because corepack is missing. Install pnpm manually or install Node with corepack available, then rerun the update command.",
  "pnpm-corepack-enable-failed":
    "Run corepack enable manually or install pnpm manually, then rerun the update command.",
  "pnpm-npm-bootstrap-failed":
    "This pnpm checkout could not bootstrap pnpm from npm automatically. Install pnpm manually, then rerun the update command.",
  "preferred-manager-unavailable":
    "Install the checkout's declared package manager manually, then rerun the update command.",
};

/** Recognizes pending projections written by this renderer, including shipped reports. */
export function isUpdateRunReportInProgress(markdown: string): boolean {
  return markdown.startsWith(IN_PROGRESS_REPORT_PREFIX);
}

type ReportInput = Pick<
  UpdateRunRecord,
  | "status"
  | "phase"
  | "reason"
  | "origin"
  | "before"
  | "after"
  | "steps"
  | "verification"
  | "repair"
  | "downtimeMs"
> &
  Partial<Pick<UpdateRunRecord, "target">>;
const PHASES = new Set<string>(UPDATE_RUN_PHASES);

type UpdateRunIdentity =
  | { kind: "unobserved" }
  | { kind: "verified" }
  | { kind: "unavailable" }
  | { kind: "mismatch"; field: "version" | "build" };

export function resolveUpdateRunIdentity(
  facts: UpdateRunRecord["verification"],
  expected: UpdateRunRecord["after"],
): UpdateRunIdentity {
  if (facts.versionMatch === undefined) {
    return { kind: "unobserved" };
  }
  if (facts.versionMatch) {
    return { kind: "verified" };
  }
  if (facts.runningVersion && expected.version && facts.runningVersion !== expected.version) {
    return { kind: "mismatch", field: "version" };
  }
  if (facts.runningBuildId && expected.buildId && facts.runningBuildId !== expected.buildId) {
    return { kind: "mismatch", field: "build" };
  }
  // Published drivers stored false for missing identity as well as disagreement.
  return { kind: "unavailable" };
}

export function formatUpdateRunIdentity(
  facts: UpdateRunRecord["verification"],
  expected: UpdateRunRecord["after"],
): string | null {
  const identity = resolveUpdateRunIdentity(facts, expected);
  if (identity.kind === "mismatch") {
    return `${identity.field} mismatch`;
  }
  return {
    unobserved: null,
    verified: "version verified",
    unavailable: "service identity unavailable",
  }[identity.kind];
}

export function formatUpdateRunCurrentHealth(health: UpdateRunReportHealth): string {
  return health.kind === "responding"
    ? `Current health: Gateway answered on the recorded port (${bounded(health.version, 120)}).`
    : "Current health unavailable; saved verification describes the update attempt only.";
}

/** Serving observation is independent of permission to restart or roll back. */
export function resolveUpdateRunVerifiedServingVersion(
  verification: UpdateRunRecord["verification"],
  observation: Pick<UpdateRunRecord["steps"][number], "failureFacts" | "exitCode"> | undefined,
): string | undefined {
  if (observation?.exitCode !== 0 || observation.failureFacts?.length) {
    return undefined;
  }
  const { recovery } = verification;
  return recovery?.serviceRestartSafe && recovery.service === "healthy"
    ? recovery.version
    : verification.versionMatch && verification.readyz && verification.settled
      ? verification.runningVersion
      : undefined;
}

/** Public-report callers redact identifiers before using this shared formatter. */
export function formatUpdateRunRecovery(
  verification: UpdateRunRecord["verification"],
  observation: Pick<UpdateRunRecord["steps"][number], "failureFacts" | "exitCode"> | undefined,
  reason = verification.recovery?.reason ?? "not-recorded",
): string | undefined {
  const { recovery } = verification;
  if (!observation) {
    if (!recovery) {
      return undefined;
    }
    const restored = recovery.packageRollbackVerified;
    if (!recovery.serviceRestartSafe) {
      return `${restored ? "package rollback verified; service restart not verified" : "not verified"} (${reason})`;
    }
    const version = bounded(recovery.version, 120);
    if (recovery.service === "healthy") {
      return `${restored ? "package rollback verified; " : ""}Gateway serving ${version}; health verified`;
    }
    if (recovery.service !== "failed" && !restored) {
      return "verified safe to restart";
    }
    const packageOutcome = restored
      ? `package rollback verified (${version})`
      : "runtime files verified";
    return `${packageOutcome}; Gateway health ${recovery.service === "failed" ? "failed" : "unverified"} (${reason}). Run \`openclaw gateway status --deep\` to check the serving version and readiness.`;
  }
  const version = resolveUpdateRunVerifiedServingVersion(verification, observation);
  if (version) {
    const constraint =
      recovery?.serviceRestartSafe === false ? `; restart remains unsafe (${reason})` : "";
    return `${recovery?.packageRollbackVerified ? "package rollback verified; " : ""}verified serving ${bounded(version, 120)}${constraint}`;
  }
  const code = observation.failureFacts?.[0]?.code;
  if (!code) {
    return "Gateway readiness is pending; recovery check completed without verified readiness";
  }
  return code === "gateway-probe-failed"
    ? `recovery check failed (${code})`
    : `not serving (${code})`;
}

function bounded(text: string, limit: number): string {
  return text.length <= limit ? text : `${sliceUtf16Safe(text, 0, limit - 1)}…`;
}

function formatUpdateVersion(identity: UpdateRunRecord["after"]): string | undefined {
  const sha = identity.sha?.slice(0, 8);
  return identity.version ? `${identity.version}${sha ? ` (${sha})` : ""}` : sha;
}

function recoveryHints(run: ReportInput, nextAction?: string): string[] {
  if (run.target?.installationMethod === "ocm") {
    return nextAction ? [] : run.origin.nextAction ? [run.origin.nextAction] : [];
  }
  if (run.status === "running") {
    return ["Check progress with openclaw update status."];
  }
  if (run.status !== "failed") {
    return [];
  }
  if (run.reason === LEGACY_UPDATE_RUN_EXPIRED_REASON) {
    return [LEGACY_UPDATE_RUN_ADVISORY];
  }
  if (run.reason === UPDATE_ACTIVATION_TIMEOUT_REASON) {
    return nextAction ? [] : [formatUpdateActivationTimeoutGuidance()];
  }
  if (run.reason === UPDATE_FOREIGN_DESTINATION_REASON) {
    return nextAction ? [] : [`Next step: ${UPDATE_DESTINATION_RECOVERY}`];
  }
  if (
    run.reason === "state-migrated-no-rollback" ||
    run.verification.rollbackOutcome?.reason === "state-migrated-no-rollback"
  ) {
    return nextAction
      ? []
      : [
          "State may have changed during migration, so rolling back code alone is unsafe. Keep the new installation and backups; inspect openclaw update status and run openclaw doctor before recovery.",
        ];
  }
  const hint =
    run.reason && Object.hasOwn(FAILURE_RECOVERY_HINTS, run.reason)
      ? FAILURE_RECOVERY_HINTS[run.reason]
      : undefined;
  const hints = hint ? [hint] : [];
  if (!nextAction) {
    hints.push(
      run.reason === "repair-failed"
        ? "Run openclaw triage to diagnose this repair failure."
        : "Run openclaw triage to diagnose and repair the failed update.",
    );
  }
  return hints;
}

/** One report for persisted update outcomes; markdown reserves room for the next action. */
export function renderUpdateRunReport(
  run: ReportInput,
  opts: {
    doctorHint?: string | null;
    nextAction?: string;
    currentHealth?: UpdateRunReportHealth;
    mode?: UpdateRunResult["mode"] | "package";
    markdownLimit?: number;
  } = {},
): UpdateRunReport {
  const reconciled = isAcknowledgedAbandonedUpdateRun(run);
  const currentHealth: UpdateRunReportHealth | undefined =
    opts.currentHealth ??
    (run.target?.installationMethod !== "ocm" &&
    run.status !== "running" &&
    opts.nextAction === undefined &&
    run.origin.nextAction
      ? { kind: "unavailable" }
      : undefined);
  // Health cannot readmit Doctor custody; report steps omit the settlement's authority fields.
  const runtimeVerificationRecovered =
    run.status === "failed" &&
    currentHealth?.kind === "responding" &&
    Boolean(run.after.version) &&
    currentHealth.version === run.after.version &&
    run.verification.recovery?.serviceRestartSafe === false &&
    run.verification.recovery.reason === "runtime-verification-failed" &&
    !run.steps.some((step) => step.step === "doctor process settlement");
  // Keep the version visible and distinguish Git updates within the same version.
  const before = formatUpdateVersion(run.before);
  const after = formatUpdateVersion(run.after);
  const reason = bounded(
    run.reason?.trim() ||
      (run.status === "failed" &&
        run.steps.find((step) => step.status === "failed" && step.step !== "requested")?.step) ||
      "unknown reason",
    240,
  );
  const running =
    !currentHealth && run.verification.serviceRunning === true
      ? run.verification.runningVersion
      : undefined;
  // These producer codes also cover unreadable runtimes and failed capability probes.
  // They do not establish that Node is old or that upgrading it will repair the update.
  const runtimeCheckFailed =
    run.status === "failed" &&
    (run.reason === "node-runtime-preflight" ||
      run.reason === "preflight-node-runtime-incompatible");
  let headline: string;
  switch (run.status) {
    case "succeeded":
      headline = after
        ? `✅ OpenClaw updated to ${after}${before ? ` (from ${before})` : ""}.`
        : "✅ OpenClaw updated.";
      break;
    case "failed":
      headline = reconciled
        ? "ℹ️ OpenClaw abandoned update reconciled."
        : run.reason === LEGACY_UPDATE_RUN_EXPIRED_REASON
          ? `ℹ️ OpenClaw update abandoned: ${reason}.`
          : runtimeCheckFailed
            ? "⚠️ OpenClaw could not complete the update. A required system check failed."
            : `⚠️ OpenClaw ${run.reason === "repair-failed" ? "repair" : "update"} failed: ${reason}.`;
      break;
    case "skipped":
      headline =
        run.reason === "still-starting"
          ? `ℹ️ OpenClaw${after ? ` ${after}` : ""} installed; Gateway still starting; readiness unverified; recovery backups retained.`
          : run.reason === "gateway-readiness-unverified"
            ? `ℹ️ OpenClaw${after ? ` ${after}` : ""} installed; Gateway readiness unverified; recovery backups retained.`
            : run.reason === "doctor-maintenance-pending"
              ? "ℹ️ OpenClaw repair pending: Doctor maintenance remains unfinished."
              : `ℹ️ OpenClaw update skipped: ${reason}.`;
      break;
    case "rolled-back":
      headline = `↩️ OpenClaw update rolled back to ${after ?? running ?? before ?? "the previous version"}: ${reason}.`;
      break;
    case "running":
      headline = `${IN_PROGRESS_REPORT_PREFIX}${run.target?.installationMethod === "ocm" ? "managed by OCM" : run.phase}.`;
      break;
  }
  headline = bounded(headline, 500);
  const markdownLimit = opts.markdownLimit ?? 1500;
  const warnings = updateRunWarningMessages(run.steps).map((message) => `Warning: ${message}`);
  // Successful activation must not bury recovery constraints behind snapshot inventory.
  // Failed runs keep their failure diagnostics ahead of warnings in the short report.
  const lines: string[] = run.status === "succeeded" ? [...warnings] : [];
  if (
    currentHealth &&
    (runtimeVerificationRecovered || (!run.origin.nextAction && !opts.nextAction))
  ) {
    lines.push(formatUpdateRunCurrentHealth(currentHealth));
    if (runtimeVerificationRecovered) {
      lines.push(
        "The target version is serving; the saved runtime verification failure no longer applies. The recorded update outcome is unchanged.",
      );
    }
  }
  if (opts.mode && opts.mode !== "unknown") {
    lines.push(`Update mode: ${opts.mode}`);
  }
  const admission = run.origin.admission;
  if (admission) {
    const candidateVersion = admission.candidateVersion
      ? ` (${bounded(admission.candidateVersion, 120)})`
      : "";
    lines.push(`Update safety checks ran in the ${admission.owner} updater${candidateVersion}.`);
    if (admission.checks?.length) {
      lines.push(
        `Admission checks: ${admission.checks.map((check) => `${bounded(check.name, 120)}: ${check.status}`).join(", ")}.`,
      );
    }
    if (admission.fallbackReason) {
      lines.push(`Admission fallback: ${bounded(admission.fallbackReason, 500)}`);
    }
  }
  for (const step of run.steps) {
    if (run.status === "running" && step.status === "in_progress" && step.detail) {
      lines.push(
        `Waiting: ${step.step}${step.startedAtMs !== undefined ? ` (started ${new Date(step.startedAtMs).toISOString()})` : ""} — ${step.detail}`,
      );
    }
    if (step.snapshotCapacity) {
      lines.push(formatUpdateSnapshotCapacity(step.snapshotCapacity));
    }
    if (
      step.detail &&
      (step.step.startsWith("diagnostic:database snapshot") ||
        step.step.startsWith("diagnostic:database migration writes") ||
        step.step.startsWith("diagnostic:database rollback"))
    ) {
      lines.push(step.detail);
    }
    if (step.configWriteRefusal) {
      lines.push(formatUpdateDoctorConfigWriteRefusal(step.configWriteRefusal));
    }
  }
  const configChanges = run.steps.flatMap((step) => (step.configChange ? [step.configChange] : []));
  const configKeys = [
    ...new Set(configChanges.flatMap((change) => (change.kind === "key" ? [change.key] : []))),
  ];
  if (configKeys.length) {
    lines.push(`Doctor changed config keys: ${configKeys.join(", ")}.`);
  }
  for (const message of new Set(
    configChanges.flatMap((change) => (change.kind === "migration" ? [change.message] : [])),
  )) {
    lines.push(`Warning: Doctor migration: ${message}`);
  }
  const phases = run.steps
    .filter((step) => PHASES.has(step.step))
    .map((step) => {
      const duration =
        step.startedAtMs != null && step.endedAtMs != null
          ? ` (${formatDurationPrecise(Math.max(0, step.endedAtMs - step.startedAtMs))})`
          : "";
      return `${step.step}${duration}`;
    });
  if (phases.length) {
    lines.push(`Phases: ${phases.join(" → ")}`);
  }
  for (const step of selectUpdateFailureReportSteps(
    run.steps.filter((item) => item.status === "failed"),
  )) {
    const failure = `Failed: ${step.step}${step.detail ? ` — ${step.detail}` : ""}`;
    lines.push(bounded(failure, 300));
    if (step.termination === "signal" && step.stderrTail) {
      lines.push(`Stderr (${step.signal ?? "unknown signal"}):\n${step.stderrTail}`);
    }
    lines.push(
      ...(step.failureFacts ?? []).slice(0, 5).map((fact) =>
        formatUpdateFailureFact({
          ...fact,
          message:
            failure.length <= 300 && fact.message && step.detail?.includes(fact.message)
              ? undefined
              : fact.message,
        }),
      ),
    );
  }
  if (run.status !== "succeeded") {
    lines.push(...warnings);
  }
  const facts = run.verification;
  const observation = run.steps.findLast((step) => step.step === "gateway recovery verification");
  const recovery =
    !runtimeVerificationRecovered && observation && formatUpdateRunRecovery(facts, observation);
  if (recovery) {
    lines.push(`Recorded recovery: ${recovery}.`);
  }
  const verification = [
    facts.booted ? "gateway booted" : undefined,
    facts.serviceRunning === undefined
      ? undefined
      : facts.serviceRunning
        ? `service running${facts.runningVersion ? ` (${bounded(facts.runningVersion, 120)})` : ""}`
        : "service stopped",
    formatUpdateRunIdentity(facts, run.after),
    facts.channelsReady === undefined
      ? undefined
      : facts.channelsReady
        ? "channels ready"
        : "channels not ready",
    facts.readyz === undefined ? undefined : facts.readyz ? "HTTP ready" : "HTTP not ready",
    facts.pluginErrors?.length
      ? `${facts.pluginErrors.length} plugin activation error(s)`
      : undefined,
  ].filter(Boolean);
  if (verification.length) {
    lines.push(`Recorded verification: ${verification.join("; ")}.`);
  }
  for (const attempt of run.repair.slice(-3)) {
    lines.push(
      bounded(
        `Repair ${attempt.attempt}: ${attempt.status}${attempt.summary || attempt.reason ? ` — ${attempt.summary ?? attempt.reason}` : ""}`,
        300,
      ),
    );
  }
  if (run.downtimeMs != null) {
    lines.push(`Gateway downtime: ${formatDurationPrecise(run.downtimeMs)}.`);
  }
  const skipGuidance =
    run.status === "skipped" &&
    run.reason &&
    Object.hasOwn(UPDATE_INSTALL_SKIP_GUIDANCE, run.reason)
      ? UPDATE_INSTALL_SKIP_GUIDANCE[run.reason]
      : undefined;
  const savedAction =
    opts.nextAction ??
    (runtimeVerificationRecovered ? undefined : run.origin.nextAction) ??
    skipGuidance;
  const nextAction =
    savedAction && currentHealth
      ? `${formatUpdateRunCurrentHealth(currentHealth)} ${
          currentHealth.kind === "responding"
            ? "This observation supersedes saved claims that the Gateway is stopped; other recovery constraints still apply. The recorded update outcome is unchanged."
            : "Check current Gateway status before acting on this saved advice."
        }\nHistorical recovery advice: “${savedAction}”`
      : savedAction;
  const lastRepairReason = run.repair.at(-1)?.reason;
  const repairStopReason =
    lastRepairReason === "requester-revoked" || lastRepairReason === "repair-requires-config-change"
      ? lastRepairReason
      : run.reason;
  const repairHint =
    run.status === "failed" && repairStopReason === "requester-revoked"
      ? nextAction
        ? "Repair stopped because the chat requester is no longer a command owner. Further recovery requires a current command owner."
        : "Repair stopped because the chat requester is no longer a command owner. A current command owner must start a new update, or the operator can run openclaw triage locally."
      : run.status === "failed" && repairStopReason === "repair-requires-config-change"
        ? nextAction
          ? "Doctor could not promote config changes. Review the named keys and writer refusal before continuing recovery."
          : "Doctor could not promote config changes. Review the named keys and writer refusal, then run openclaw doctor --fix under your own authority, or openclaw triage."
        : undefined;
  const hints = reconciled
    ? []
    : run.status === "running"
      ? opts.nextAction
        ? [opts.nextAction]
        : recoveryHints(run)
      : repairHint
        ? [repairHint, ...(nextAction ? [nextAction] : [])]
        : [
            ...new Set(
              [
                // Install ownership refusals need the deployment workflow, not Doctor repair.
                skipGuidance
                  ? undefined
                  : (opts.doctorHint ?? facts.doctorHint ?? run.origin.doctorHint),
                ...recoveryHints(run, nextAction),
                nextAction,
              ].filter((line): line is string => Boolean(line)),
            ),
          ];
  const next = hints.at(-1);
  if (runtimeCheckFailed || run.reason === "doctor-maintenance-pending") {
    // Keep the owner's selected action ahead of the diagnostic dump, including
    // historical-advice qualifications. Neither this layout nor truncation selects recovery.
    const details = [
      "Details:",
      `Reason code: ${reason}`,
      ...lines,
      ...hints.filter((line) => line !== next),
    ];
    const lead = [headline, ...(next ? [bounded(next, 1100)] : []), ""].join("\n");
    return {
      headline,
      lines: [...(next ? [next, ""] : []), ...details],
      markdown: `${lead}\n${bounded(details.join("\n"), markdownLimit - lead.length - 1)}`,
    };
  }
  lines.push(...hints);
  const body = [headline, ...lines.filter((line) => line !== next)].join("\n");
  const suffix = next ? `\n${bounded(next, 1100)}` : "";
  return { headline, lines, markdown: `${bounded(body, markdownLimit - suffix.length)}${suffix}` };
}

/** Old CLI finalization paths still return runner results; all wording stays in the report. */
export function updateRunReportInputFromResult(
  result: UpdateRunResult,
  recorded?: Partial<ReportInput>,
): ReportInput {
  const steps = result.steps.flatMap(updateRunStepsFromResultStep);
  const observationStep = (name: string) =>
    name === "gateway verification" || name === "gateway recovery verification";
  const observations = steps.filter((entry) => observationStep(entry.step));
  const preserveRecorded = result.status === "ok" && result.verification === undefined;
  const { booted, noticeDelivered, doctorHint, recovery, rollbackOutcome } =
    recorded?.verification ?? {};
  const resultStatus =
    result.status === "ok" ? "succeeded" : result.status === "error" ? "failed" : "skipped";
  // Failed diagnostics may not have reached the ledger yet.
  const recordedOutcome = result.status === "error" ? undefined : recorded;
  return {
    status:
      recordedOutcome?.status ??
      (recorded?.status === "rolled-back" && isVerifiedUpdateRollback(result)
        ? "rolled-back"
        : resultStatus),
    phase: recordedOutcome?.phase ?? "finished",
    reason:
      recordedOutcome?.reason !== undefined
        ? recordedOutcome.reason
        : (result.reason ?? recorded?.reason ?? null),
    origin: recorded?.origin ?? {},
    before: recordedOutcome?.before ?? result.before ?? recorded?.before ?? {},
    after: recordedOutcome?.after ?? result.after ?? recorded?.after ?? {},
    repair: recorded?.repair ?? [],
    downtimeMs: recorded?.downtimeMs ?? null,
    verification:
      preserveRecorded && recorded?.verification
        ? recorded.verification
        : {
            ...(result.verification ?? recorded?.verification),
            ...(recorded?.verification ? { booted, noticeDelivered, doctorHint } : {}),
            recovery:
              recovery?.serviceRestartSafe === false
                ? recovery
                : (result.recovery ?? (result.verification === undefined ? recovery : undefined)),
            rollbackOutcome: result.rollbackOutcome ?? rollbackOutcome,
          },
    steps: !recorded?.steps
      ? steps
      : !preserveRecorded && (result.verification !== undefined || observations.length)
        ? [...recorded.steps.filter((entry) => !observationStep(entry.step)), ...observations]
        : recorded.steps,
  };
}

/** Stable releases can leave a pre-ledger sentinel across an upgrade. */
export function updateRunReportInputFromSentinel(payload: RestartSentinelPayload): ReportInput {
  const stats = payload.stats;
  const version = (value: Record<string, unknown> | null | undefined) => ({
    ...(typeof value?.version === "string" ? { version: value.version } : {}),
    ...(typeof value?.sha === "string" ? { sha: value.sha } : {}),
  });
  const pending =
    payload.status === "skipped" &&
    (stats?.reason === "managed-service-handoff-started" ||
      stats?.reason === "restart-health-pending");
  return {
    status: pending
      ? "running"
      : payload.status === "ok"
        ? "succeeded"
        : payload.status === "error"
          ? "failed"
          : "skipped",
    phase: pending ? "restarting" : "finished",
    reason: stats?.reason ?? null,
    origin: payload.doctorHint ? { doctorHint: payload.doctorHint } : {},
    before: version(stats?.before),
    after: version(stats?.after),
    verification: {},
    repair: [],
    downtimeMs: null,
    steps: (stats?.steps ?? []).map((step) => ({
      step: step.name,
      status: step.log?.exitCode === 0 ? "completed" : "failed",
      failureFacts: step.failureFacts,
    })),
  };
}
